import { useState, useEffect, ReactNode } from "react";
import { invoke } from "../lib/bridge";
import { DocMeta } from "../App";
import { DeleteButton } from "./DeleteButton";
import { Button } from "./ui/Button";
import { ConfirmDialog } from "./ui/ConfirmDialog";
import { EmptyState } from "./ui/EmptyState";

interface DocListTabProps {
  docType: string;
  title: string;
  onOpen: (doc: DocMeta) => void;
  syncVersion?: number;
  /** Placeholder text for the new-item title input. Defaults to "<singular> name...". */
  inputPlaceholder?: string;
  /** Optional renderer for extra content inside each list item (e.g. a thumb image). */
  renderItemExtra?: (doc: DocMeta) => ReactNode;
}

export function DocListTab({ docType, title, onOpen, syncVersion, inputPlaceholder, renderItemExtra }: DocListTabProps) {
  const [docs, setDocs] = useState<DocMeta[]>([]);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<DocMeta | null>(null);

  const singularTitle = title.replace(/s$/, "");
  const lowerTitle = title.toLowerCase();
  const placeholder = inputPlaceholder ?? `${singularTitle} name...`;

  const loadDocs = async () => {
    try {
      const result = await invoke<DocMeta[]>("list_documents", { docType });
      setDocs(result);
    } catch (e) {
      console.error(`Failed to load ${lowerTitle}:`, e);
    }
  };

  useEffect(() => {
    loadDocs();
  }, [syncVersion]);

  const createDoc = async () => {
    const trimmed = newTitle.trim();
    if (!trimmed) return;
    try {
      const doc = await invoke<DocMeta>("create_document", {
        docType,
        title: trimmed,
      });
      setNewTitle("");
      setCreating(false);
      onOpen(doc);
    } catch (e) {
      console.error(`Failed to create ${singularTitle.toLowerCase()}:`, e);
    }
  };

  const doDelete = async (doc: DocMeta) => {
    try {
      await invoke("delete_document", { docId: doc.docId });
      setDocs(docs.filter((d) => d.docId !== doc.docId));
    } catch (e) {
      console.error(`Failed to delete ${singularTitle.toLowerCase()}:`, e);
    }
    setConfirmDelete(null);
  };

  return (
    <div className="container">
      <h1>{title}</h1>

      {docs.length > 0 && (
        <ul className="doc-list">
          {docs.map((doc) => (
            <li key={doc.docId} className="doc-list-item">
              <button
                className="doc-list-button"
                onClick={() => onOpen(doc)}
                style={{ textAlign: "left", display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span>{doc.title}</span>
                {renderItemExtra && renderItemExtra(doc)}
              </button>
              <DeleteButton onClick={() => setConfirmDelete(doc)} />
            </li>
          ))}
        </ul>
      )}

      {docs.length === 0 && !creating && (
        <EmptyState>No {lowerTitle} yet. Create one below.</EmptyState>
      )}

      {creating ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createDoc();
          }}
          className="add-form"
        >
          <input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder={placeholder}
            className="add-input"
          />
          <Button type="submit">Create</Button>
          <Button
            variant="secondary"
            onClick={() => { setCreating(false); setNewTitle(""); }}
          >
            Cancel
          </Button>
        </form>
      ) : (
        <Button
          size="full"
          onClick={() => setCreating(true)}
        >
          + New {singularTitle}
        </Button>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        message={confirmDelete ? `Delete "${confirmDelete.title}"?` : ""}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={() => {
          if (confirmDelete) doDelete(confirmDelete);
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
